import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins } from 'lucide-react';

import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useResyncCryptoOnchainPayment } from '@/lib/api/crypto-onchain-platform';

import { RecentPaymentRow } from './RecentPaymentRow';
import type { PlatformPayment } from './types';

type Props = {
  payments: PlatformPayment[];
};

const WAIVED_ONLY_SWITCH_ID = 'crypto-payments-waived-only';

/** crypto-api settled the payment without taking the platform share, and said why. */
function isPlatformShareWaived(payment: PlatformPayment): boolean {
  return !!payment.settlement?.waivedReason;
}

/**
 * The last payments across every store, with what crypto-api settled to the
 * platform. The platform share column is the one operators reconcile against
 * the platform wallets; the waived-share column and its filter surface the
 * revenue the platform did not take (nobody else sees those notifications).
 * "Re-sync" re-reads one crypto-api session when a webhook went missing.
 */
export function RecentPaymentsTable({ payments }: Props) {
  const { t } = useTranslation('manageCryptoPayments');
  const resync = useResyncCryptoOnchainPayment();
  const [isWaivedOnly, setIsWaivedOnly] = useState(false);

  const waivedCount = payments.filter(isPlatformShareWaived).length;
  const visiblePayments = isWaivedOnly ? payments.filter(isPlatformShareWaived) : payments;
  // One re-sync at a time: the button of the row in flight waits, the others stay clickable afterwards.
  const resyncingPaymentId = resync.isPending ? (resync.variables?.cryptoOnchainPaymentId ?? null) : null;

  const handleResync = (cryptoOnchainPaymentId: string) => {
    if (resync.isPending) return;
    resync.mutate({ cryptoOnchainPaymentId });
  };

  if (payments.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Coins />
          </EmptyMedia>
          <EmptyTitle>{t('payments.empty')}</EmptyTitle>
          <EmptyDescription>{t('payments.emptyDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <Switch id={WAIVED_ONLY_SWITCH_ID} checked={isWaivedOnly} onCheckedChange={setIsWaivedOnly} />
        <Label htmlFor={WAIVED_ONLY_SWITCH_ID} className="text-sm font-normal">
          {t('payments.waivedOnly', { waived: waivedCount })}
        </Label>
      </div>

      {visiblePayments.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('payments.waivedOnlyEmpty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('payments.columns.store')}</TableHead>
              <TableHead>{t('payments.columns.order')}</TableHead>
              <TableHead>{t('payments.columns.amount')}</TableHead>
              <TableHead>{t('payments.columns.fiat')}</TableHead>
              <TableHead>{t('payments.columns.status')}</TableHead>
              <TableHead>{t('payments.columns.platformShare')}</TableHead>
              <TableHead>{t('payments.columns.waivedShare')}</TableHead>
              <TableHead>{t('payments.columns.txHash')}</TableHead>
              <TableHead>{t('payments.columns.createdAt')}</TableHead>
              <TableHead className="text-right">{t('payments.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visiblePayments.map((payment) => (
              <RecentPaymentRow
                key={payment.id}
                payment={payment}
                isResyncing={resyncingPaymentId === payment.id}
                onResync={handleResync}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
